import { attributes, html, joinClasses } from 'BomdisoftComponents/html/index';
import { excellent, good, ok, poor, ratingSelectorT, ratingValueT, type SupportedLanguage, useless } from '@bomdisoft/translations';

export type RatingSize = 'large' | 'medium' | 'small';

export type RatingOptions = {
  className?: string;
  language?: SupportedLanguage;
  name: string;
  rating?: number;
  readOnly?: boolean;
  showLabels?: boolean;
  size?: RatingSize;
};

export const RATING_STEPS: readonly number[] = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];

export const RATING_SIZE_PX: Readonly<Record<RatingSize, number>> = { large: 30, medium: 24, small: 18 };

export const STAR_PATH = 'M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z';

const RATING_WORDS = [useless, poor, ok, good, excellent];

export const ratingLabel = (value: number, language: SupportedLanguage) => {
  const word = RATING_STEPS.includes(value) ? RATING_WORDS[Math.ceil(value) - 1]?.[language] : undefined;
  if (word === undefined) return '';
  return Number.isInteger(value) ? `${word}+` : word;
};

export const toStarFill = (star: number, value: number) => (value >= star ? 1 : value >= star - 0.5 ? 0.5 : 0);

const STAR_GLYPH = html`<svg aria-hidden="true" fill="currentColor" viewBox="0 0 24 24"><path d="${STAR_PATH}"></path></svg>`;

const renderStar = (fill?: number) => html`<span class="bomdisoft-rating-star">${STAR_GLYPH}<span${attributes({ class: 'bomdisoft-rating-star-fill', style: fill === undefined ? undefined : `--star-fill: ${fill}` })}>${STAR_GLYPH}</span></span>`;

const STARS = [1, 2, 3, 4, 5];

const renderHalf = (step: number, name: string, value: number) =>
  html`<label${attributes({ class: 'bomdisoft-rating-half', 'data-side': Number.isInteger(step) ? 'right' : 'left', 'data-star': Math.ceil(step), 'data-value': step })}><input${attributes({ 'aria-label': `${step} stars`, checked: value === step, name, type: 'radio', value: step })}></label>`;

export const renderRating = ({ className, language = 'english', name, rating = 0, readOnly = true, showLabels = true, size = 'medium' }: RatingOptions) => {
  const holderClass = joinClasses('bomdisoft-rating', className);
  const sizeStyle = `--rating-size: ${RATING_SIZE_PX[size]}px`;
  if (readOnly) {
    return html`<span${attributes({ 'aria-label': ratingValueT[language].replace('{value}', String(rating)), class: holderClass, 'data-size': size, role: 'img', style: sizeStyle })}>${STARS.map(star => renderStar(toStarFill(star, rating)))}${showLabels ? html`<span class="bomdisoft-rating-label">${ratingLabel(rating, language)}</span>` : ''}</span>`;
  }
  return html`<span${attributes({ class: holderClass, 'data-size': size, style: sizeStyle })}><fieldset${attributes({ 'aria-label': ratingSelectorT[language], class: 'bomdisoft-rating-group' })}>${STARS.map(() => renderStar())}${RATING_STEPS.map(step => renderHalf(step, name, rating))}</fieldset>${showLabels ? html`<span aria-live="polite" class="bomdisoft-rating-label">${RATING_STEPS.map(step => html`<span${attributes({ 'data-value': step })}>${ratingLabel(step, language)}</span>`)}</span>` : ''}</span>`;
};
